import {
  AccountsService,
  PlanName,
  PricingTableKey,
} from 'base/accounts_service';
import { AccountStore } from 'base/account_store';
import { AnalyticsService } from 'base/analytics';
import { getCookie } from 'base/api_host';
import { UnreachableError } from 'base/helpers';
import { posthogIdentify } from 'base/posthog';
import { useDocumentTitle } from 'base/ui/document_title';
import {
  EXTRA_LONG_TOAST_DURATION,
  ToastLayer,
  ToastQueue,
} from 'base/ui/toast';
import { mergeSearchParams } from 'base/url';
import { ChoosePlan } from 'blocks/auth/plans';
import { getBillingInterval, getPlanName } from 'blocks/billing/plan_utils';
import {
  planChosenToRedirectUrl,
  PLAN_UPGRADED_SEARCH_PARAM,
} from 'blocks/manage_account/manage_plan';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { getUserJwt, saveUserJwt } from '../../../library/src/modules/cookies';
import {
  AuthFlowInviteTeamForm,
  ChangePasswordForm,
  ForgotPasswordForm,
  LoginForm,
  SignupForm,
  SignupSurveyForm,
  VerifyEmailForm,
} from './forms';
import { SharedFormState } from './form_state';
import { AuthLayout, AuthNavbar } from './layout';
import {
  AuthRoute,
  AuthRouteNoContext,
  authRouteToUrl,
  consumeSearchParam,
  getCurrentLocationForRedirect,
  INVALID_AUTH_TOAST_ID,
  TOAST_ID_SEARCH_PARAM,
} from './routes';

const PLAN_NAMES = [
  PlanName.FREE,
  PlanName.STARTER,
  PlanName.PRO,
  PlanName.TEAM,
];

// this is being used in webflow to persist the referral code into the session
const REFERRAL_CODE = 'REFERRAL_CODE';
const getReferralCode = () => {
  const params = new URLSearchParams(window.location.search);
  return (
    params.get('referralCode') ||
    localStorage.getItem(REFERRAL_CODE) ||
    undefined
  );
};

/** Renders all the auth pages, so it can smoothly transition and share form state. */
export const AuthFlowPages = observer(function _AuthFlowPages({
  route: rawRoute,
  accountsService,
  accountStore,
  analytics,
}: {
  route: AuthRoute;
  accountsService: AccountsService;
  accountStore: AccountStore;
  analytics: AnalyticsService;
}) {
  const route = maybePromoteSelectPlanToDialog(rawRoute) || rawRoute;

  const [formState] = React.useState(() => new SharedFormState());
  const [toastQueue] = React.useState(() => new ToastQueue());
  const [isFinishing, setIsFinishing] = React.useState(false);
  const finshGotAccessTokenTasks = React.useRef<Promise<unknown>[]>([]);
  const currentPlanName = accountStore.member?.planName ?? PlanName.FREE;

  function goTo(to: AuthRouteNoContext) {
    goToOverrideContext({ ...to, context: route.context });
  }
  function goToOverrideContext(to: AuthRoute) {
    window.history.pushState({}, '', authRouteToUrl(to));
  }

  function gotAccessToken(token: string) {
    saveUserJwt(token);
    const {
      context: { oauthKey },
    } = route;
    if (oauthKey) {
      const promise = accountsService.linkOAuth({ writeKey: oauthKey, token });
      finshGotAccessTokenTasks.current.push(promise);
    }
  }

  function finishFlow(planChosen?: PricingTableKey) {
    setIsFinishing(true);
    const chosenPlanName = getPlanName(planChosen ?? 'free');
    let redirect =
      route.context.redirect ?? planChosenToRedirectUrl(planChosen);
    if (
      planChosen != null &&
      PLAN_NAMES.indexOf(currentPlanName) < PLAN_NAMES.indexOf(chosenPlanName)
    ) {
      redirect = mergeSearchParams(redirect, {
        [PLAN_UPGRADED_SEARCH_PARAM]: planChosen,
      }).toString();
    }

    const removeToast = toastQueue.addPersistent(() => ({
      label: 'Getting things set up...',
    }));
    Promise.allSettled(finshGotAccessTokenTasks.current).then(() => {
      removeToast();
      // We always reload the app after authing or upgrading.  Never pushState.
      window.location.href = redirect;
    });
  }

  function continueAfterLogin(accessToken: string) {
    gotAccessToken(accessToken);
    if (route.context.selectPlan) {
      goTo({ path: 'upgrade' });
    } else {
      finishFlow();
    }
  }

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [route.path]);

  const shouldCheckAlreadyLoggedIn =
    route.path === 'login' || route.path === 'signup';
  React.useEffect(
    () => {
      if (!shouldCheckAlreadyLoggedIn) return;
      checkAlreadyLoggedIn(toastQueue, accountsService).then((token) => {
        token && continueAfterLogin(token);
      });
    }, // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shouldCheckAlreadyLoggedIn],
  );

  const requiresLogin = route.path === 'upgrade';
  React.useEffect(() => {
    if (!requiresLogin) return;
    if (getUserJwt()) return;
    goToOverrideContext({
      path: 'login',
      context: { redirect: getCurrentLocationForRedirect() },
    });
  }, [requiresLogin]);

  // Show toast if user has been logged out due to invalid auth
  React.useEffect(() => {
    const toastId = consumeSearchParam(TOAST_ID_SEARCH_PARAM);
    if (toastId !== INVALID_AUTH_TOAST_ID) {
      return;
    }

    toastQueue.push({
      label:
        'You have been logged out due to recent access from another location.',
      duration: EXTRA_LONG_TOAST_DURATION,
    });
  }, [toastQueue]);

  let content;
  let title;

  if (route.path === 'signup') {
    title = 'Signup';
    content = (
      <SignupForm
        formState={formState}
        goToLogin={() => {
          analytics.track({
            event: 'signup_flow:login_button_click',
          });
          goTo({ path: 'login' });
        }}
        submitSignupForm={async () => {
          const email = formState.email;
          const referralCode = getReferralCode();
          const res = await accountsService.signup({
            ...formState,
            signupReferral: getCookie('rtag'),
            fbc: getCookie('fbclid'),
            fbp: getCookie('_fbp'),
            gclid: getCookie('gclid'),
            referralCode,
          });
          analytics.track({
            event: 'signup_flow:signup_form_submit',
            referral_code: referralCode,
          });
          goTo({
            path: 'signup/verify-email',
            userId: res.userId,
            email,
          });
        }}
      />
    );
  } else if (route.path === 'signup/verify-email') {
    title = 'Signup - Verify Email';
    content = (
      <VerifyEmailForm
        email={route.email}
        handleVerify={async (code) => {
          const res = await accountsService.verifyEmail({
            email: route.email,
            code,
          });
          gotAccessToken(res.accessToken);
          const me = await accountStore.get(true);
          if (me) {
            posthogIdentify(me);
          }
          // without waiting for posthog identify we will filter these events out in the sync
          // process between Customer IO and Posthog as we only sync identified user events
          analytics.track({
            event: 'signup_flow:verify_email_submit',
          });
          // route.context.selectedPlan is not set for the free plan see PaidPricingTableKey
          if (route.context.skipPricingTable) {
            goTo({ path: 'signup/survey' });
          } else {
            goTo({ path: 'signup/choose-plan' });
          }
        }}
        resendCode={async () => {
          await accountsService.requestVerificationCode({ email: route.email });
        }}
      />
    );
  } else if (route.path === 'signup/choose-plan') {
    title = 'Signup - Choose Plan';
    content = (
      <ChoosePlan
        mustChoosePlan={true}
        accountsService={accountsService}
        accountStore={accountStore}
        dialogState={route.dialog}
        setDialogState={(dialog) =>
          goTo({ path: 'signup/choose-plan', dialog })
        }
        onSelectPlan={(planKey) =>
          analytics.track({
            event: 'signup_flow:start_button_click',
            plan_name: getPlanName(planKey),
            billing_interval: getBillingInterval(planKey),
          })
        }
        onDone={(planKey, hasCoupon, urlState) => {
          const planName = getPlanName(planKey ?? 'free');
          if (planName !== PlanName.FREE) {
            analytics.track({
              event: 'signup_flow:payment_form_submit',
              plan_name: planName,
              billing_interval: getBillingInterval(planKey),
              has_coupon: hasCoupon,
            });
          }
          if (
            planName !== PlanName.FREE &&
            urlState &&
            urlState?.currentPayment &&
            urlState?.currentPayment <= 0
          ) {
            analytics.track({
              event: 'signup_flow:trial_started',
              plan_name: planName,
              billing_interval: getBillingInterval(planKey),
            });
          }
          if (
            planName !== PlanName.FREE &&
            urlState &&
            urlState?.currentPayment &&
            urlState?.currentPayment > 0
          ) {
            analytics.track({
              event: 'signup_flow:subscription_started',
              plan_name: planName,
              billing_interval: getBillingInterval(planKey),
            });
          }
          if (planKey === 'teamYearly' || planKey === 'teamMonthly') {
            goTo({ path: 'signup/invite-team' });
          } else {
            goTo({ path: 'signup/survey' });
          }
        }}
      />
    );
  } else if (route.path === 'signup/invite-team') {
    title = 'Signup - Invite Team';
    content = (
      <AuthFlowInviteTeamForm
        accountsService={accountsService}
        toastQueue={toastQueue}
        onDone={() => goTo({ path: 'signup/survey' })}
      />
    );
  } else if (route.path === 'signup/survey') {
    title = 'Signup';
    content = (
      <SignupSurveyForm
        submitSignupSurveyForm={async (data) => {
          await accountsService.updateAttributes(data);
          void analytics.track({ event: 'signup_flow:survey_submit', data });
          finishFlow();
        }}
      />
    );
  } else if (route.path === 'login') {
    title = 'Login';
    content = (
      <LoginForm
        formState={formState}
        goToSignup={() => goTo({ path: 'signup' })}
        goToForgotPassword={() => goTo({ path: 'forgot-password' })}
        submitLoginForm={async () => {
          const res = await accountsService.login(formState);
          continueAfterLogin(res.accessToken);
        }}
      />
    );
  } else if (route.path === 'forgot-password') {
    title = 'Forgot Password';
    content = (
      <ForgotPasswordForm
        formState={formState}
        goToLogin={() => goTo({ path: 'login' })}
        submitForgotPasswordForm={async () => {
          const email = formState.email;
          const res = await accountsService.requestVerificationCode({ email });
          goTo({
            path: 'forgot-password/verify-email',
            email,
          });
        }}
      />
    );
  } else if (route.path === 'forgot-password/verify-email') {
    title = 'Forgot Password';
    content = (
      <VerifyEmailForm
        email={route.email}
        handleVerify={async (code) => {
          const res = await accountsService.verifyEmail({
            email: route.email,
            code,
          });
          gotAccessToken(res.accessToken);
          goTo({
            path: 'forgot-password/change-password',
            entitlement: res.resetPasswordEntitlement,
          });
        }}
        resendCode={async () => {
          await accountsService.requestVerificationCode({ email: route.email });
        }}
      />
    );
  } else if (route.path === 'forgot-password/change-password') {
    title = 'Change Password';
    content = (
      <ChangePasswordForm
        formState={formState}
        submitChangePasswordForm={async () => {
          await accountsService.resetPassword({
            entitlement: route.entitlement,
            newPassword: formState.password,
          });
          if (route.context.selectPlan) {
            goTo({ path: 'upgrade' });
          } else {
            finishFlow();
          }
        }}
        skipChangePassword={() => finishFlow()}
      />
    );
  } else if (route.path === 'upgrade') {
    title = 'Upgrade';
    content = (
      <ChoosePlan
        mustChoosePlan={false}
        accountStore={accountStore}
        accountsService={accountsService}
        dialogState={route.dialog}
        setDialogState={(dialog) => goTo({ path: 'upgrade', dialog })}
        onDone={(planChosen, hasCoupon, urlState) => {
          const planName = getPlanName(planChosen ?? 'free');
          if (planName !== PlanName.FREE) {
            analytics.track({
              event: 'upgrade_flow:payment_form_submit',
              plan_name: planName,
              billing_interval: getBillingInterval(planChosen),
              has_coupon: hasCoupon,
            });
          }
          if (
            planName !== PlanName.FREE &&
            urlState &&
            urlState?.currentPayment != null &&
            urlState?.currentPayment <= 0
          ) {
            analytics.track({
              event: 'upgrade_flow:trial_started',
              plan_name: planName,
              billing_interval: getBillingInterval(planChosen),
            });
          }
          if (
            planName !== PlanName.FREE &&
            urlState &&
            urlState?.currentPayment != null &&
            urlState?.currentPayment > 0
          ) {
            analytics.track({
              event: 'upgrade_flow:subscription_started',
              plan_name: planName,
              billing_interval: getBillingInterval(planChosen),
            });
          }

          finishFlow(planChosen);
        }}
        onSelectPlan={(planKey) => {
          analytics.track({
            event: 'upgrade_flow:upgrade_button_click',
            plan_name: getPlanName(planKey),
            billing_interval: getBillingInterval(planKey),
          });
        }}
      />
    );
  } else {
    throw new UnreachableError(route);
  }

  useDocumentTitle(`${title} - Relume`);

  const isPlanPath =
    route.path === 'signup/choose-plan' || route.path === 'upgrade';
  return (
    <>
      <AuthNavbar />
      <AuthLayout
        showQuote={route.path === 'signup'}
        contentFullWidth={isPlanPath}
        transitionKey={route.path}
        isFinishing={isFinishing}
      >
        {content}
      </AuthLayout>
      <ToastLayer queue={toastQueue} />
    </>
  );
});

async function checkAlreadyLoggedIn(
  toastQueue: ToastQueue,
  accountService: AccountsService,
): Promise<string | undefined> {
  const token = getUserJwt();
  if (!token) return;

  const removeToast = toastQueue.addPersistent(() => ({
    label: `Checking if you're already logged in...`,
  }));
  try {
    const me = await accountService.me();
    if (!me.isActive) {
      // they'll be asked to verify their email when they log in
      return;
    }
    return getUserJwt();
  } catch (err) {
    return;
  } finally {
    removeToast();
  }
}

function maybePromoteSelectPlanToDialog(
  route: AuthRoute,
): AuthRoute | undefined {
  if (
    !(route.path === 'signup/choose-plan' || route.path === 'upgrade') ||
    route.dialog != null
  ) {
    return;
  }
  const { selectPlan } = route.context;
  if (!selectPlan) return;
  return {
    ...route,
    dialog: { planKey: selectPlan },
    context: { ...route.context, selectPlan: undefined },
  };
}
